import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { FaMinimize, FaMaximize } from 'react-icons/fa6'
import defaultTexts from '../../../texts/texts'
import constants from '../../../config/constants'
import { request } from '../../../_network/request'
import { serviceConfigsURLs } from '../../../_network/apiUrls'
import CustomBackdrop from '../../_common/CustomBackdrop'
import PermissionsView from './PermissionsView'
import PermissionsEdit from './PermissionsEdit'

const PermissionsWrapper = (props) => {
  const { backendBillingServices, setFetchServices } = props
  const theme = useTheme()
  
  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [expanded, setExpanded] = useState(true)
  const [editMode, setEditMode] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [billingPermissions, setBillingPermissions] = useState(null)

  const wrapperStyle = {
    background: 'white',
    borderRadius: '50px 20px 20px 20px',
    boxShadow: '2px 2px 2px rgba(0, 0, 0, 0.25)',
    marginBottom: '40px',
  }

  const headStyle = {
    width: '100%',
    height: '60px',
  }

  const bodyStyle = {
    padding: '20px',
  }

  const labelStyle = {
    height: '60px',
    background: theme.palette.greenSheen,
    color: 'white',
    fontSize: '20px',
    fontWeight: '500',
    borderRadius: '50px 0px',
    padding: '0px 40px',    
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  const editButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.vodka,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.vodka, 0.8),
    },
  }

  const saveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    textTransform: 'capitalize',
    marginTop: '20px',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const getDefaultBillingPermissions = () => {
    return {
      visible_to_service: ['custom_admin_ui', 'custom_client_ui'],
      custom_admin_ui: [
        {
          section: 'billing_configs',
          permissions: ['read'],
        },
        {
          section: 'billing_services',
          permissions: ['read', 'create', 'update', 'delete'],
        },
        {
          section: 'billing_deposits',
          permissions: ['read', 'update'],
        },
        {
          section: 'billing_prices',
          permissions: ['read', 'create', 'update'],
        },
        {
          section: 'billing_analytics',
          permissions: ['read'],
        },
      ]
    }
  }

  const clientServiceConfigsRequest = async (postData) => {
    const record_id = backendBillingServices.length > 0 ? backendBillingServices[0].id : null
    const url = constants.core_service_domain + '/' + serviceConfigsURLs.getClientServiceConfigs + '/' + record_id
    const request_data = { url, method: 'PUT', data: postData }
    await request(request_data)
  }

  const handleClickEdit = () => {
    setEditMode(true)
  }

  const handleClickSave = async () => {
    setIsLoading(true)

    const postData = {
      config_params: billingPermissions.config_params,
    }

    await clientServiceConfigsRequest(postData)

    setFetchServices(prev => prev + 1)
    setEditMode(false)
    setIsLoading(false)
  }
  
  useEffect(() => {
    if (backendBillingServices.length > 0) {
      setBillingPermissions(backendBillingServices[0])
    } else {
      setBillingPermissions(null)
    }    
  }, [backendBillingServices])
  
  return (
    <Box sx={wrapperStyle}>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={headStyle}
      >
        <Box sx={labelStyle}>
          <Box>{defaultTexts.permissions}</Box>
        </Box>
        <Box sx={{ paddingRight: '10px ' }}>
          {
            expanded ?
            <IconButton onClick={() => setExpanded(false)}>
              <FaMinimize color={theme.palette.greenSheen} />
            </IconButton> :
            <IconButton onClick={() => setExpanded(true)}>
              <FaMaximize color={theme.palette.greenSheen} />
            </IconButton>
          }
        </Box>
      </Stack>

      {
        expanded &&
        <Box sx={bodyStyle}>
          {
            editMode ?
            <PermissionsEdit
              billingPermissions={billingPermissions}
              setBillingPermissions={setBillingPermissions}
              getDefaultBillingPermissions={getDefaultBillingPermissions}
            /> :
            <PermissionsView
              billingPermissions={backendBillingServices.length > 0 ? backendBillingServices[0] : null}
            />
          }

          {
            editPermission &&
            <Stack
              direction='row'
              justifyContent='flex-end'
            >
              {
                editMode ?
                <Button
                  variant='contained'
                  disableRipple
                  disableElevation
                  disabled={false}
                  onClick={handleClickSave}
                  sx={saveButtonStyle}
                >
                  {defaultTexts.save}
                </Button> :
                <Button
                  variant='contained'
                  disableRipple
                  disableElevation
                  disabled={false}
                  onClick={handleClickEdit}
                  sx={editButtonStyle}
                >
                  {defaultTexts.edit}
                </Button>
              }            
            </Stack>
          }          
        </Box>
      }
    </Box>
  )
}

export default PermissionsWrapper